import { Box, Slider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

interface ChatLockDurationSliderProps {
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}

const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    const hours = minutes / 60;

    if (hours === Math.floor(hours)) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return `${hours.toFixed(1)} hours`;
};

// Fixed preset values
const PRESET_VALUES = [1, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440];

const ChatLockDurationSlider = ({ value, onChange, disabled = false }: ChatLockDurationSliderProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Find the index of the closest preset value
    const getPresetIndex = (minutes: number): number => {
        const closest = PRESET_VALUES.reduce((prev, curr) => (Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev));
        return PRESET_VALUES.indexOf(closest);
    };

    // Create marks using indices (evenly spaced)
    // On small screens, only show every other label
    const marks = PRESET_VALUES.map((val, index) => {
        const showLabel = isSmallScreen ? index % 2 === 0 : true;
        const label = val < 60 ? `${val}m` : val === 60 ? '1h' : val === 90 ? '1.5h' : `${val / 60}h`;

        return {
            value: index,
            label: showLabel ? label : '',
        };
    });

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        const index = newValue as number;
        onChange(PRESET_VALUES[index]);
    };

    // Get the current index
    const currentIndex = getPresetIndex(value);

    return (
        <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">Keep the user in the list for...</Typography>
                <Box
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                        padding: theme.spacing(1, 2),
                        borderRadius: theme.shape.borderRadius,
                        minWidth: '120px',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                        }}
                    >
                        {formatDuration(PRESET_VALUES[currentIndex])}
                    </Typography>
                </Box>
            </Stack>

            <Typography variant="body2" color="textSecondary">
                This is how long the user will be kept in the list and "locked" for sending messages to the user.
            </Typography>

            <Box sx={{ px: 1 }}>
                <Slider
                    value={currentIndex}
                    onChange={handleSliderChange}
                    min={0}
                    max={PRESET_VALUES.length - 1}
                    step={1}
                    marks={marks}
                    disabled={disabled}
                    valueLabelDisplay="off"
                    valueLabelFormat={index => formatDuration(PRESET_VALUES[index])}
                    sx={{
                        '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                        },
                        '& .MuiSlider-mark': {
                            backgroundColor: theme.palette.divider,
                            height: 8,
                        },
                        '& .MuiSlider-markActive': {
                            backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiSlider-thumb': {
                            width: 20,
                            height: 20,
                        },
                    }}
                />
            </Box>
        </Stack>
    );
};

export default ChatLockDurationSlider;
